import { useState } from "react";
import { Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { memberKey, nextPalette } from "../../lib/team";

const GROUP_LABEL = { board: "Board of directors", advisor: "Advisory board" };
const field = "mt-1 w-full rounded-md border border-border bg-sand px-3 py-2 text-[14px] text-ink outline-none focus:border-ink";
const label = "text-[11px] font-semibold uppercase tracking-[0.18em] text-olive";

/**
 * "Add a person" form for /admin/team. Creates a board member or advisor
 * record (`custom: true`) that lands at the end of its group on /team.
 * `people` = current members of the chosen group (for the palette rotation
 * and the duplicate-name check); `takenKeys` = every key on the page.
 */
export const AddTeamMemberDialog = ({ open, onOpenChange, group, people, takenKeys, client, onCreated }) => {
  const [form, setForm] = useState({ name: "", role: "", school: "", bio: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const key = memberKey(form.name);
  const duplicate = key.length >= 2 && takenKeys.has(key);
  const ready = form.name.trim().length >= 2 && form.role.trim().length >= 2 && !duplicate;

  const submit = async (e) => {
    e.preventDefault();
    if (!ready) return;
    setSaving(true);
    try {
      const res = await client.post("/admin/team/members", { ...form, group, ...nextPalette(people) });
      onCreated(res.data.member_key, res.data.member);
      toast.success(`${form.name.trim()} added to the ${GROUP_LABEL[group].toLowerCase()}.`, { description: "Live on /team now. Add photos from their card." });
      setForm({ name: "", role: "", school: "", bio: "" });
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't add this person.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-bone" data-testid="add-team-member-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-normal text-ink">Add a person to the {GROUP_LABEL[group].toLowerCase()}</DialogTitle>
          <DialogDescription className="text-[13px] text-olive">
            They appear at the end of the group on /team right away, with a monogram tile until you upload photos.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <label className="block">
            <span className={label}>Full name</span>
            <input value={form.name} onChange={set("name")} maxLength={80} required autoFocus data-testid="add-member-name" className={`${field} font-serif text-lg`} />
            {duplicate && <span className="mt-1 block text-[11px] text-scarlet" data-testid="add-member-duplicate">Someone with that name is already on the Team page.</span>}
          </label>
          <label className="block">
            <span className={label}>Role</span>
            <input value={form.role} onChange={set("role")} maxLength={120} required placeholder="Board Member" data-testid="add-member-role" className={`${field} font-serif text-lg italic`} />
          </label>
          <label className="block">
            <span className={label}>Alma mater <span className="normal-case tracking-normal">(polaroid caption, optional)</span></span>
            <input value={form.school} onChange={set("school")} maxLength={80} placeholder="Spelman College" data-testid="add-member-school" className={field} />
          </label>
          <label className="block">
            <span className={label}>Bio <span className="normal-case tracking-normal">(optional, blank line between paragraphs)</span></span>
            <textarea value={form.bio} onChange={set("bio")} maxLength={6000} rows={5} data-testid="add-member-bio" className={`${field} resize-y leading-relaxed`} />
          </label>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={() => onOpenChange(false)} data-testid="add-member-cancel"
              className="rounded-full px-4 py-2 text-[12px] font-semibold text-olive transition hover:text-ink">Cancel</button>
            <button type="submit" disabled={!ready || saving} data-testid="add-member-submit"
              className="inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 text-[12px] font-semibold text-sand transition hover:bg-forest disabled:opacity-50">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" strokeWidth={2} />}
              Add to Team page
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
